/**
 * Shared product-card colour swatches.
 *
 * One controller for every product card on the site. Rendered by
 * snippets/card-swatches.liquid, which emits a <product-card-swatches> element
 * containing radio inputs plus a JSON payload describing each option value's
 * variant, image and (Liquid-formatted) price.
 *
 * Because this is a custom element, cards injected after page load upgrade
 * themselves. That covers facet filtering (facets.js replaces the whole grid),
 * <product-recommendations>, quick-add modals and Swiper loop clones without any
 * re-init hook or MutationObserver.
 */
(function () {
  'use strict';

  if (window.__cardSwatchesLoaded) return;
  window.__cardSwatchesLoaded = true;

  var instanceCount = 0;

  /* Resolve the first matching element for a list of selectors, in priority order. */
  function pick(root, selectors) {
    if (!root) return null;
    for (var i = 0; i < selectors.length; i++) {
      var el = root.querySelector(selectors[i]);
      if (el) return el;
    }
    return null;
  }

  var CARD_ROOT_SELECTOR = '.card-wrapper, .product-card, .grid__item, .swiper-slide';

  var PRIMARY_IMAGE_SELECTORS = [
    '[data-card-primary-image]',
    '.primary-img',
    '.card__media img',
    '.cs-product-image img'
  ];

  var SECONDARY_IMAGE_SELECTORS = ['[data-card-secondary-image]', '.hover-img'];

  var MEDIA_WRAPPER_SELECTORS = ['.card__media', '.cs-product-image', '.media'];

  var NO_HOVER_SWAP = 'card-swatches--no-hover-swap';

  /* Remember an image element well enough to put it back exactly as it was. */
  function snapshot(img) {
    return {
      src: img.getAttribute('src') || '',
      srcset: img.getAttribute('srcset') || '',
      width: img.getAttribute('width') || '',
      height: img.getAttribute('height') || '',
      alt: img.getAttribute('alt') || ''
    };
  }

  class CardSwatchesElement extends HTMLElement {
    connectedCallback() {
      if (this.__initialised) return;
      this.__initialised = true;

      this.card = this.closest(CARD_ROOT_SELECTOR) || this.parentElement;
      this.data = this.readData();
      this.inputs = Array.prototype.slice.call(this.querySelectorAll('.card-swatch__input'));

      if (!this.inputs.length) return;

      // Make the radio group name unique per instance. Swiper loop clones and any
      // card rendered twice on one page would otherwise share a name and fight
      // over the checked state.
      var suffix = '-i' + ++instanceCount;
      this.inputs.forEach(function (input) {
        input.name += suffix;
      });

      this.captureDefaults();

      // The card renders one swatch already selected, so the price must describe
      // that colour rather than the product-wide minimum. Price only: touching the
      // image here would trigger a needless second download on every card.
      var checked = this.querySelector('.card-swatch__input:checked');
      if (checked) {
        var entry = this.entryFor(checked);
        if (entry) this.updatePrice(entry);
      }

      // If Liquid could not resolve a selected value, mark the first selectable
      // swatch without swapping anything - the card already shows product defaults.
      if (!this.querySelector('.card-swatch__input:checked')) {
        for (var i = 0; i < this.inputs.length; i++) {
          if (!this.inputs[i].disabled) {
            this.inputs[i].checked = true;
            this.syncSelectedState(this.inputs[i]);
            break;
          }
        }
      }
    }

    readData() {
      var script = this.querySelector('.card-swatches__data');
      if (!script) return [];
      try {
        var parsed = JSON.parse(script.textContent);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    }

    captureDefaults() {
      var img = pick(this.card, PRIMARY_IMAGE_SELECTORS);
      if (img) this.defaultImage = snapshot(img);
      var hover = pick(this.card, SECONDARY_IMAGE_SELECTORS);
      if (hover) this.defaultHoverImage = snapshot(hover);
    }

    /** Entry point: apply the option value described by `input`. */
    /* The payload entry for a swatch input, looked up by index. */
    entryFor(input) {
      var index = parseInt(input.dataset.swatchIndex, 10);
      for (var i = 0; i < this.data.length; i++) {
        // Look up by index, never by colour name. Names with spaces, punctuation,
        // accents or near-duplicates ("Gold" vs "Gold Plated") cannot collide.
        if (this.data[i].index === index) return this.data[i];
      }
      return null;
    }

    /** Entry point: apply the option value described by `input`. */
    select(input) {
      if (!input || input.disabled) return;
      var entry = this.entryFor(input);
      if (!entry) return;

      this.syncSelectedState(input);
      this.updateImage(entry);
      this.updatePrice(entry);
      this.updateVariantInputs(entry);
      this.updateLinks(entry);
    }

    syncSelectedState(input) {
      this.querySelectorAll('.card-swatch').forEach(function (label) {
        label.classList.remove('is-selected');
      });
      var label = input.closest('.card-swatch');
      if (label) label.classList.add('is-selected');

      var display = this.querySelector('.card-swatches__selected-value');
      if (display) display.textContent = input.dataset.optionValue || input.value || '';
    }

    updateImage(entry) {
      var img = pick(this.card, PRIMARY_IMAGE_SELECTORS);
      if (!img) return;

      var media = pick(this.card, MEDIA_WRAPPER_SELECTORS);
      var hover = pick(this.card, SECONDARY_IMAGE_SELECTORS);

      if (!entry.image) {
        // This colour has no image of its own: restore the card defaults, including
        // the original hover image, and let the normal hover effect run again.
        if (this.defaultImage) this.applyImage(img, this.defaultImage);
        if (hover && this.defaultHoverImage) this.applyImage(hover, this.defaultHoverImage);
        if (media) media.classList.remove(NO_HOVER_SWAP);
        return;
      }

      this.applyImage(img, {
        src: entry.image,
        srcset: entry.srcset || '',
        width: entry.width || '',
        height: entry.height || '',
        alt: entry.alt || ''
      });

      if (!hover || !media) return;

      if (entry.hover_image) {
        // This colour has a follow-up shot of its own, so hover keeps working.
        this.applyImage(hover, {
          src: entry.hover_image,
          srcset: entry.hover_srcset || '',
          width: entry.width || '',
          height: entry.height || '',
          alt: entry.alt || ''
        });
        media.classList.remove(NO_HOVER_SWAP);
      } else {
        // No second shot for this colour. The theme fades the primary image out on
        // hover, so leaving the stale hover image in place would show another
        // colour, and hiding it outright left a blank white box. Disable the swap
        // instead: the selected colour simply stays put on hover.
        media.classList.add(NO_HOVER_SWAP);
      }
    }

    applyImage(img, spec) {
      if (spec.srcset) {
        img.setAttribute('srcset', spec.srcset);
      } else {
        img.removeAttribute('srcset');
      }
      img.setAttribute('src', spec.src);
      if (spec.width) img.setAttribute('width', spec.width);
      if (spec.height) img.setAttribute('height', spec.height);
      if (spec.alt) img.setAttribute('alt', spec.alt);
    }

    updatePrice(entry) {
      if (!this.card || !entry.price) return;

      // Markup A: snippets/price.liquid output (card-product and everything that
      // renders it - collection grid, search results, related, complementary,
      // featured collection, collage, footer, mega menu).
      this.card.querySelectorAll('.price').forEach(function (priceEl) {
        priceEl.querySelectorAll('.price__regular .price-item--regular').forEach(function (node) {
          node.innerHTML = entry.price;
        });
        priceEl.querySelectorAll('.price__sale .price-item--sale').forEach(function (node) {
          node.innerHTML = entry.price;
        });

        var compareNodes = priceEl.querySelectorAll('.price__compare-price .price-item--regular');
        compareNodes.forEach(function (node) {
          node.innerHTML = entry.compare_at_price || '';
        });

        priceEl.classList.toggle('price--on-sale', !!entry.on_sale);
        priceEl.classList.toggle('price--sold-out', entry.available === false);
      });

      // Markup B: the inline cards in cs-collection-tabs and collection-earnings.
      this.card.querySelectorAll('.product-price-row').forEach(function (row) {
        var regular = row.querySelector('.col-price');
        if (regular) regular.innerHTML = entry.price;

        var compare = row.querySelector('.col-com-price');
        if (compare) {
          if (entry.compare_at_price) {
            compare.innerHTML = entry.compare_at_price;
            compare.hidden = false;
          } else {
            compare.innerHTML = '';
            compare.hidden = true;
          }
        }
      });
    }

    updateVariantInputs(entry) {
      if (!this.card) return;
      // Preserves the existing quick-add / product-form contract.
      this.card.querySelectorAll('input[name="id"]').forEach(function (input) {
        if (String(input.value) === String(entry.variant_id)) return;
        input.value = entry.variant_id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    updateLinks(entry) {
      if (!this.card || !entry.variant_id) return;
      // Point the product link at the selected variant without clobbering the rest of
      // the query string. Search and collection links already carry _pos/_sid/_ss
      // tracking params, so the variant has to be merged in rather than appended.
      this.card.querySelectorAll('a[href*="/products/"]').forEach(function (link) {
        var href = link.getAttribute('href');
        if (!href) return;
        try {
          var url = new URL(href, window.location.origin);
          url.searchParams.set('variant', entry.variant_id);
          link.setAttribute(
            'href',
            url.pathname + url.search + url.hash
          );
        } catch (e) {
          if (entry.url) link.setAttribute('href', entry.url);
        }
      });
    }
  }

  if (!window.customElements.get('product-card-swatches')) {
    window.customElements.define('product-card-swatches', CardSwatchesElement);
  }

  /* A single delegated listener for the whole document - no per-card bindings. */
  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input || !input.classList || !input.classList.contains('card-swatch__input')) return;
    var host = input.closest('product-card-swatches');
    if (host && typeof host.select === 'function') host.select(input);
  });

  /* Stop swatch clicks reaching the card's full-media overlay link. */
  document.addEventListener('click', function (event) {
    if (event.target.closest && event.target.closest('.card-swatch')) {
      event.stopPropagation();
    }
  });
})();
